import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ProductsComponent from '@shared/components/products';
import * as PropTypes from 'prop-types';
import { DataMap } from '@shared/utils/dataMapping';
import { getProductsList } from '@shared/utils/detailUtils';
import { TelemetryImage } from '@shared/components/telemetryImage';
import { Stack, Text, mergeStyleSets, FontIcon, StackItem, Separator } from '@fluentui/react';
import React, { Fragment } from 'react';
import classNames from 'classnames';
import { NeutralColors, SharedColors } from '@fluentui/theme';
import { logger } from '@src/logger';
import { Constants } from '@shared/utils/constants';
import { SpzaInstrumentService } from '@shared/services/telemetry/spza/spzaInstrument';
import { routes } from '@shared/routerHistory';
import { Link, useLocation } from 'react-router-dom';
import { RatingsInfo } from '@shared/components/ratings/ratingsInfo';
const containerTokens = {
    childrenGap: 24,
    padding: 20
};
const itemsTokens = {
    childrenGap: 8
};
const itemsInsideLinkedTokens = {
    childrenGap: 8
};
const titleAndStarsStackToken = {
    childrenGap: 8
};
const parentStackTokens = {
    childrenGap: 8,
    padding: 8
};
const appTileLinkedItemsStyles = mergeStyleSets({
    itemImage: {
        width: 60,
        height: 60,
        display: 'flex',
        backgroundColor: NeutralColors.white
    },
    textWeight: {
        fontWeight: 600
    },
    linkItemTitle: {
        fontWeight: 600,
        color: NeutralColors.gray160,
        overflow: 'hidden',
        '-webkit-box-orient': 'vertical',
        display: '-webkit-box',
        '-webkit-line-clamp': '2'
    },
    linkItemContainer: {
        justifyContent: 'space-around'
    },
    moreProductsStyle: {
        paddingTop: 3
    },
    imageStackItemStyle: {
        '& .appTileHoverImage': {
            width: 40,
            height: 40,
            margin: 'auto'
        }
    },
    starsAndRattingContainer: {
        '& .ratingInfoSection .detailsRatingStar': {
            marginBottom: 0
        }
    },
    rattingText: {
        color: NeutralColors.gray130
    },
    rattingStarText: {
        marginRight: 4,
        padding: 0,
        fontSize: 12
    },
    iconClass: {
        height: 16,
        width: 16,
        color: SharedColors.cyanBlue10
    }
});
const seperatorStyle = {
    root: [
        {
            selectors: {
                '::before': {
                    background: NeutralColors.gray40
                }
            }
        }
    ]
};
const tileStyle = {
    root: {
        selectors: {
            '&:hover': {
                backgroundColor: NeutralColors.gray10
            }
        }
    }
};
const getProducts = (products)=>{
    var _ref;
    return (_ref = products && Object.keys(products).length > 0 && getProductsList(products)) !== null && _ref !== void 0 ? _ref : [];
};
const getRatingsData = ({ context, ratingsAverage, ratingsNum })=>{
    return ratingsNum >= 1 && {
        avgRating: ratingsAverage,
        ratingsCount: ratingsNum,
        key: 'internal',
        text: context.loc('Tile_ASRatings', 'ratings')
    };
};
const getLinkedItemLink = ({ linkedItem, context })=>{
    const { entityId, linkedSaaS } = linkedItem;
    const productId = linkedSaaS ? DataMap.products.AzureforWebApps.UrlKey : DataMap.products.Office365.UrlKey;
    return context.buildHref(routes.appDetails, {
        productId,
        entityId
    }, {
        tab: 'Overview'
    });
};
const logTelemetryInfo = ({ linkedItem, hoveredItem, location })=>{
    const instrument = SpzaInstrumentService.getProvider();
    const { title, entityId } = linkedItem;
    const { title: hoveredTitle, entityId: hoveredEntityId } = hoveredItem;
    const payload = {
        page: location.pathname,
        action: Constants.Telemetry.Action.Click,
        actionModifier: Constants.Telemetry.ActionModifier.SaasLinkTooltip,
        details: JSON.stringify({
            title,
            entityId,
            hoveredTitle,
            hoveredEntityId
        })
    };
    instrument.probe(Constants.Telemetry.ProbeName.LogInfo, payload);
    logger.info('', payload);
};
const LinkedItem = ({ linkedItem, hoveredItem }, context)=>{
    const location = useLocation();
    const itemProducts = getProducts(linkedItem.products);
    const ratingsData = getRatingsData({
        context,
        ratingsAverage: linkedItem.AverageRating,
        ratingsNum: linkedItem.NumberOfRatings
    });
    return /*#__PURE__*/ _jsx(Link, {
        to: getLinkedItemLink({
            linkedItem,
            context
        }),
        onClick: ()=>{
            logTelemetryInfo({
                linkedItem,
                hoveredItem,
                location
            });
        },
        children: /*#__PURE__*/ _jsx(Stack, {
            tokens: parentStackTokens,
            styles: tileStyle,
            children: /*#__PURE__*/ _jsx(Stack.Item, {
                children: /*#__PURE__*/ _jsxs(Stack, {
                    horizontal: true,
                    tokens: itemsInsideLinkedTokens,
                    children: [
                        /*#__PURE__*/ _jsx(Stack.Item, {
                            className: classNames([
                                appTileLinkedItemsStyles.imageStackItemStyle
                            ]),
                            align: "center",
                            children: /*#__PURE__*/ _jsx(TelemetryImage, {
                                src: linkedItem.iconURL,
                                alt: context.loc('Linked_Item_Icon', 'linked item icon'),
                                className: classNames([
                                    appTileLinkedItemsStyles.itemImage
                                ], 'ms-depth-8'),
                                imgClassName: "appTileHoverImage"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Stack.Item, {
                            grow: 1,
                            children: /*#__PURE__*/ _jsxs(Stack, {
                                tokens: titleAndStarsStackToken,
                                className: appTileLinkedItemsStyles.linkItemContainer,
                                children: [
                                    /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            variant: "mediumPlus",
                                            block: true,
                                            className: classNames([
                                                appTileLinkedItemsStyles.linkItemTitle
                                            ]),
                                            children: linkedItem.title
                                        })
                                    }),
                                    (itemProducts === null || itemProducts === void 0 ? void 0 : itemProducts.length) && /*#__PURE__*/ _jsx(Stack.Item, {
                                        grow: 1,
                                        className: classNames([
                                            appTileLinkedItemsStyles.moreProductsStyle
                                        ]),
                                        children: /*#__PURE__*/ _jsx(ProductsComponent, {
                                            textSize: "smallPlus",
                                            withText: true,
                                            productsItems: itemProducts
                                        })
                                    }),
                                    ratingsData && /*#__PURE__*/ _jsx(Stack.Item, {
                                        className: classNames([
                                            appTileLinkedItemsStyles.starsAndRattingContainer
                                        ]),
                                        children: /*#__PURE__*/ _jsx(RatingsInfo, {
                                            avgRating: ratingsData.avgRating,
                                            ratingsCount: ratingsData.ratingsCount,
                                            title: ratingsData.text,
                                            context: context,
                                            textSize: "smallPlus",
                                            starClass: classNames([
                                                appTileLinkedItemsStyles.rattingStarText
                                            ]),
                                            ratingClass: classNames([
                                                appTileLinkedItemsStyles.rattingText
                                            ])
                                        }, ratingsData.key)
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Stack.Item, {
                            align: "center",
                            children: /*#__PURE__*/ _jsx(FontIcon, {
                                iconName: "ChevronRight",
                                className: appTileLinkedItemsStyles.iconClass
                            })
                        })
                    ]
                })
            })
        })
    });
};
export const AppTileLinkedItems = ({ title, linkedItems, hoveredItem })=>{
    return /*#__PURE__*/ _jsxs(Stack, {
        tokens: containerTokens,
        children: [
            /*#__PURE__*/ _jsx(Stack.Item, {
                children: title && /*#__PURE__*/ _jsx(Text, {
                    variant: "medium",
                    className: appTileLinkedItemsStyles.textWeight,
                    children: title
                })
            }),
            /*#__PURE__*/ _jsx(Stack.Item, {
                children: /*#__PURE__*/ _jsx(Stack, {
                    tokens: itemsTokens,
                    children: linkedItems === null || linkedItems === void 0 ? void 0 : linkedItems.map((item, index, linkedItems)=>/*#__PURE__*/ _jsxs(Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Stack.Item, {
                                    children: /*#__PURE__*/ _jsx(LinkedItem, {
                                        linkedItem: item,
                                        hoveredItem: hoveredItem
                                    })
                                }),
                                index + 1 !== linkedItems.length && /*#__PURE__*/ _jsx(Stack.Item, {
                                    children: /*#__PURE__*/ _jsx(Stack, {
                                        children: /*#__PURE__*/ _jsx(StackItem, {
                                            children: /*#__PURE__*/ _jsx(Separator, {
                                                styles: seperatorStyle
                                            })
                                        })
                                    })
                                })
                            ]
                        }, index))
                })
            })
        ]
    });
};
LinkedItem.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func,
    buildHref: PropTypes.func
};
